import { getAuthHeaders } from "../lib/auth";
import { useState, useEffect, useCallback } from "react";
import PharmatechLayout from "../components/PharmatechLayout";
import { History, AlertTriangle, CheckCircle2, AlertCircle, Stethoscope, Users, Filter } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const getHeaders = getAuthHeaders;

const FEU_COLORS = {
  vert: { bg: "bg-green-50", border: "border-green-200", text: "text-green-700", icon: CheckCircle2 },
  orange: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", icon: AlertTriangle },
  rouge: { bg: "bg-red-50", border: "border-red-200", text: "text-red-700", icon: AlertCircle },
};

export default function CopilotHistoryPage() {
  const [analyses, setAnalyses] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/pharmatech/copilot/history?skip=${page * 20}&limit=20`, { headers: getHeaders() });
      setAnalyses(r.data.analyses || []);
      setTotal(r.data.total || 0);
    } catch {}
  }, [page]);

  useEffect(() => { load(); }, [load]);

  const filtered = filter === "all" ? analyses : analyses.filter(a => a.feu === filter);

  return (
    <PharmatechLayout title="Historique des Analyses" subtitle="Toutes vos analyses Copilot et Simulateur">
      <div data-testid="copilot-history-page" className="max-w-4xl mx-auto space-y-6">
        {/* Stats bar */}
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-600"><span className="font-bold text-slate-800">{total}</span> analyses au total</p>
          <div className="flex gap-2">
            {[{ key: "all", label: "Toutes" }, { key: "vert", label: "Vert" }, { key: "orange", label: "Orange" }, { key: "rouge", label: "Rouge" }].map(f => (
              <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`filter-${f.key}`}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${filter === f.key ? "bg-blue-50 text-blue-700 border-blue-300" : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"}`}>
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* List */}
        <div className="space-y-3">
          {filtered.map((a, i) => {
            const cfg = FEU_COLORS[a.feu] || FEU_COLORS.vert;
            const Icon = cfg.icon;
            const iscopilot = a.type === "copilot";
            return (
              <div key={i} className={`p-4 rounded-xl bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow`} data-testid={`history-item-${i}`}>
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${cfg.bg} ${cfg.border} border`}>
                    <Icon className={`w-5 h-5 ${cfg.text}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase ${cfg.bg} ${cfg.text} ${cfg.border} border`}>{a.feu}</span>
                      <span className="text-xs font-medium text-slate-400 flex items-center gap-1">
                        {iscopilot ? <Stethoscope className="w-3 h-3" /> : <Users className="w-3 h-3" />}
                        {iscopilot ? "Copilot" : "Simulateur"}
                      </span>
                      <span className="text-xs text-slate-400 ml-auto">{new Date(a.timestamp).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                    </div>
                    <p className="text-sm font-medium text-slate-800 truncate">
                      {(a.drugs || a.new_drugs || []).join(", ") || "Analyse"}
                    </p>
                    <div className="flex gap-3 mt-2 text-[10px] text-slate-500">
                      <span>Score: <strong className={cfg.text}>{a.risk_score}/100</strong></span>
                      {a.interactions_count !== undefined && <span>Interactions: {a.interactions_count}</span>}
                      {a.ci_count !== undefined && <span>CI: {a.ci_count}</span>}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="text-center py-12">
              <History className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="text-sm text-slate-500">Aucune analyse {filter !== "all" ? `avec feu ${filter}` : ""} pour le moment</p>
              <p className="text-xs text-slate-400 mt-1">Utilisez le Copilot ou le Simulateur Patient pour commencer</p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {total > 20 && (
          <div className="flex justify-center gap-2">
            <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0}
              className="px-4 py-2 rounded-lg text-xs font-medium border border-slate-200 bg-white text-slate-600 disabled:opacity-50 hover:bg-slate-50">Precedent</button>
            <span className="px-4 py-2 text-xs text-slate-500">Page {page + 1} / {Math.ceil(total / 20)}</span>
            <button onClick={() => setPage(page + 1)} disabled={(page + 1) * 20 >= total}
              className="px-4 py-2 rounded-lg text-xs font-medium border border-slate-200 bg-white text-slate-600 disabled:opacity-50 hover:bg-slate-50">Suivant</button>
          </div>
        )}
      </div>
    </PharmatechLayout>
  );
}
